import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  generateOTPAPI,
  registerAPI,
  verifyOTPAPI,
} from "../services/public/authService";
import Navbar from "../components/Navbar";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../utility/ToastMessage";
import { RoutePaths } from "../utility/RoutePaths";

const Register = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);
  const [otpRetryTimer, setOtpRetryTimer] = useState(0);

  const [formData, setFormData] = useState({
    // Step 1: Personal Details
    firstName: "",
    lastName: "",
    nic: "",
    email: "",
    birthday: "",
    gender: "",
    addressLine1: "",
    street: "",
    city: "",
    postalCode: "",
    isPhysical: "",
    institute: "",
    district: "",
    batch: "",
    school: "",

    // Step 2: Guardian Details
    guardianName: "",
    relationship: "",
    guardianAddress: "",
    guardianContactNo: "",

    // Step 3: Login Details
    mobileNo: "",
    password: "",
    verifyPassword: "",

    // Step 4: Agreement
    agreementAccepted: false,
    userLevelId: 3,
  });

  // Timer effect for OTP retry
  useEffect(() => {
    let interval = null;
    if (otpRetryTimer > 0) {
      interval = setInterval(() => {
        setOtpRetryTimer((prevTimer) => {
          if (prevTimer <= 1) {
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [otpRetryTimer]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First Name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last Name is required";
    }
    if (!formData.nic.trim()) {
      newErrors.nic = "NIC is required";
    }
    if (!formData.birthday) {
      newErrors.birthday = "Birthday is required";
    }
    if (!formData.gender) {
      newErrors.gender = "Gender is required";
    }
    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = "Address Line 1 is required";
    }
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }
    if (!formData.isPhysical) {
      newErrors.isPhysical = "Please select Physical or Online";
    }
    if (formData.isPhysical === "physical" && !formData.institute) {
      newErrors.institute = "Institute is required";
    }
    if (!formData.district) {
      newErrors.district = "District is required";
    }
    if (!formData.batch) {
      newErrors.batch = "Batch is required";
    }
    if (!formData.school.trim()) {
      newErrors.school = "School is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!formData.guardianName.trim()) {
      newErrors.guardianName = "Guardian Name is required";
    }
    if (!formData.relationship.trim()) {
      newErrors.relationship = "Guardian Relationship is required";
    }
    if (!formData.guardianContactNo.trim()) {
      newErrors.guardianContactNo = "Guardian Contact Number is required";
    } else if (!/^[0-9+\-\s()]+$/.test(formData.guardianContactNo)) {
      newErrors.guardianContactNo = "Please enter a valid contact number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};

    if (!formData.mobileNo.trim()) {
      newErrors.mobileNo = "Mobile Number is required";
    } else if (!/^[0-9+\-\s()]+$/.test(formData.mobileNo)) {
      newErrors.mobileNo = "Please enter a valid mobile number";
    }
    if (!isMobileVerified) {
      newErrors.mobileVerification = "Please verify your mobile number";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }
    if (!formData.verifyPassword) {
      newErrors.verifyPassword = "Please verify your password";
    } else if (formData.password !== formData.verifyPassword) {
      newErrors.verifyPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep4 = () => {
    if (!formData.agreementAccepted) {
      setErrors({
        agreementAccepted:
          "You must accept the terms and conditions to continue",
      });
      return false;
    }
    return true;
  };

  const handleSendOTP = async () => {
    if (!formData.mobileNo.trim()) {
      setErrors({ mobileNo: "Please enter your mobile number first" });
      return;
    }
    if (!/^[0-9+\-\s()]+$/.test(formData.mobileNo)) {
      setErrors({ mobileNo: "Please enter a valid mobile number" });
      return;
    }

    setOtpSending(true);
    var params = {
      MobileNo: formData.mobileNo,
    };
    const response = await generateOTPAPI(params);

    if (response.IsSuccessful) {
      setOtpSent(true);
      setOtpRetryTimer(120); // Start 2-minute timer (120 seconds)
      showSuccessToastMsg(
        `OTP sent to ${formData.mobileNo}. Verify to continue.`,
      );
    } else {
      showErrorToastMsg("Failed to send OTP. Please try again.");
    }
    setOtpSending(false);
  };

  const handleVerifyOTP = async () => {
    if (!verificationCode.trim()) {
      setErrors({ verificationCode: "Please enter the verification code" });
      return;
    }

    setOtpVerifying(true);
    var params = {
      mobileNo: formData.mobileNo,
      otpCode: verificationCode,
    };
    const response = await verifyOTPAPI(params);

    if (response.IsSuccessful) {
      setIsMobileVerified(true);
      setErrors({});
      showSuccessToastMsg(
        "Mobile number verified successfully!. You can proceed to set your password.",
      );
    } else {
      showErrorToastMsg("Invalid verification code. Please try again.");
    }
    setOtpVerifying(false);
  };

  const handleNext = () => {
    let isValid = false;

    if (currentStep === 1) {
      isValid = validateStep1();
    } else if (currentStep === 2) {
      isValid = validateStep2();
    } else if (currentStep === 3) {
      isValid = validateStep3();
    }

    if (isValid) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep4()) {
      return;
    }

    setIsLoading(true);

    try {
      formData.isPhysical = formData.isPhysical === "1" ? true : false;
      // Here you would make an API call to register the user
      const response = await registerAPI(formData);

      if (response.IsSuccessful) {
        navigate(RoutePaths.LOGIN);
      } else {
        setErrors({ submit: "Registration failed. Please try again." });
      }
    } catch (error) {
      console.error("Registration error:", error);
      setErrors({ submit: "Registration failed. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const steps = [
    { number: 1, title: "Personal Details" },
    { number: 2, title: "Guardian Details" },
    { number: 3, title: "Login Details" },
    { number: 4, title: "Agreement" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Navbar />
      <div className="max-w-4xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="images/logo.png"
            alt="royal econ"
            className="w-24 h-24 mx-auto"
          />
          <h2 className="text-3xl font-extrabold text-gray-900">
            Student Registration
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Create your account in a few simple steps
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                      currentStep >= step.number
                        ? "bg-primary-600 border-primary-600 text-white"
                        : "bg-white border-gray-300 text-gray-500"
                    }`}
                  >
                    {currentStep > step.number ? (
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      <span className="font-semibold">{step.number}</span>
                    )}
                  </div>
                  <div className="ml-3 hidden sm:block">
                    <p
                      className={`text-sm font-medium ${
                        currentStep >= step.number
                          ? "text-primary-600"
                          : "text-gray-500"
                      }`}
                    >
                      {step.title}
                    </p>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      currentStep > step.number
                        ? "bg-primary-600"
                        : "bg-gray-300"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          {errors.submit && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              {errors.submit}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Step 1: Personal Details */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Personal Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.firstName ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Kasun"
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.lastName ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Perera"
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.lastName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="nic"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      NIC <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="nic"
                      name="nic"
                      value={formData.nic}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.nic ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="123456789V"
                    />
                    {errors.nic && (
                      <p className="mt-1 text-sm text-red-500">{errors.nic}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.email ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="email@email.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="birthday"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Birthday <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="birthday"
                      name="birthday"
                      value={formData.birthday}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.birthday ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.birthday && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.birthday}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="gender"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.gender ? "border-red-500" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select Gender</option>
                      <option value="1">Male</option>
                      <option value="2">Female</option>
                    </select>
                    {errors.gender && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.gender}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="addressLine1"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Address Line 1 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="addressLine1"
                      name="addressLine1"
                      value={formData.addressLine1}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.addressLine1
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="No 23/2"
                    />
                    {errors.addressLine1 && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.addressLine1}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="street"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Street
                    </label>
                    <input
                      type="text"
                      id="street"
                      name="street"
                      value={formData.street}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.street ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Darmapala Mawatha"
                    />
                    {errors.street && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.street}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="city"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.city ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Colombo"
                    />
                    {errors.city && (
                      <p className="mt-1 text-sm text-red-500">{errors.city}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="postalCode"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Postal Code
                    </label>
                    <input
                      type="text"
                      id="postalCode"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.city ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="234343"
                    />
                    {errors.city && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.postalCode}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="isPhysical"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Class Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="isPhysical"
                      name="isPhysical"
                      value={formData.isPhysical}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.isPhysical ? "border-red-500" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select Class Type</option>
                      <option value="1">Physical</option>
                      <option value="0">Online</option>
                    </select>
                    {errors.isPhysical && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.isPhysical}
                      </p>
                    )}
                  </div>

                  {formData.isPhysical === "1" && (
                    <div>
                      <label
                        htmlFor="institute"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Institute <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="institute"
                        name="institute"
                        value={formData.institute}
                        onChange={handleChange}
                        className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                          errors.institute ? "border-red-500" : "border-gray-300"
                        }`}
                      >
                        <option value="">Select Institute</option>
                        <option value="Science Center - Kegalle">Science Center - Kegalle</option>
                        <option value="Sesip - Rambukkana">Sesip - Rambukkana</option>
                        <option value="Unique - Matale">Unique - Matale</option>
                      </select>
                      {errors.institute && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.institute}
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label
                      htmlFor="district"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      District <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="district"
                      name="district"
                      value={formData.district}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.district ? "border-red-500" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select District</option>
                      <option value="Ampara">Ampara</option>
                      <option value="Anuradhapura">Anuradhapura</option>
                      <option value="Badulla">Badulla</option>
                      <option value="Batticaloa">Batticaloa</option>
                      <option value="Colombo">Colombo</option>
                      <option value="Galle">Galle</option>
                      <option value="Gampaha">Gampaha</option>
                      <option value="Hambantota">Hambantota</option>
                      <option value="Jaffna">Jaffna</option>
                      <option value="Kalutara">Kalutara</option>
                      <option value="Kandy">Kandy</option>
                      <option value="Kegalle">Kegalle</option>
                      <option value="Kilinochchi">Kilinochchi</option>
                      <option value="Kurunegala">Kurunegala</option>
                      <option value="Mannar">Mannar</option>
                      <option value="Matale">Matale</option>
                      <option value="Matara">Matara</option>
                      <option value="Moneragala">Moneragala</option>
                      <option value="Mullaitivu">Mullaitivu</option>
                      <option value="Nuwara Eliya">Nuwara Eliya</option>
                      <option value="Polonnaruwa">Polonnaruwa</option>
                      <option value="Puttalam">Puttalam</option>
                      <option value="Ratnapura">Ratnapura</option>
                      <option value="Trincomalee">Trincomalee</option>
                      <option value="Vavuniya">Vavuniya</option>
                    </select>
                    {errors.district && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.district}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="batch"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Batch <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="batch"
                      name="batch"
                      value={formData.batch}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.batch ? "border-red-500" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select Batch</option>
                      <option value="Batch 2026">Batch 2026</option>
                      <option value="Batch 2027">Batch 2027</option>
                      <option value="Batch 2028">Batch 2028</option>
                    </select>
                    {errors.batch && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.batch}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="school"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      School <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="school"
                      name="school"
                      value={formData.school}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.school ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Enter your school name"
                    />
                    {errors.school && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.school}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Guardian Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Parent Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="guardianName"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Parent Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="guardianName"
                      name="guardianName"
                      value={formData.guardianName}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.guardianName
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Enter parent's full name"
                    />
                    {errors.guardianName && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.guardianName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="relationship"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Relationship <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="relationship"
                      name="relationship"
                      value={formData.relationship}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.relationship
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    >
                      <option value="">Select Relationship</option>
                      <option value="father">Father</option>
                      <option value="mother">Mother</option>
                      <option value="guardian">Guardian</option>
                    </select>
                    {errors.relationship && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.relationship}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="guardianContactNo"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Parent Contact Number{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="guardianContactNo"
                      name="guardianContactNo"
                      value={formData.guardianContactNo}
                      onChange={handleChange}
                      className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                        errors.guardianContactNo
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Enter contact number"
                    />
                    {errors.guardianContactNo && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.guardianContactNo}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Login Details */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Login Credentials
                </h3>

                <div className="space-y-6">
                  <div>
                    <label
                      htmlFor="mobileNo"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        id="mobileNo"
                        name="mobileNo"
                        value={formData.mobileNo}
                        onChange={handleChange}
                        className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                          errors.mobileNo ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="Enter mobile number"
                        disabled={isMobileVerified}
                      />
                      {!isMobileVerified && (
                        <button
                          type="button"
                          onClick={handleSendOTP}
                          className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition duration-300 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={otpSending || otpRetryTimer > 0}
                        >
                          {otpSending
                            ? "Sending..."
                            : otpRetryTimer > 0
                              ? `Retry in ${Math.floor(otpRetryTimer / 60)}:${(otpRetryTimer % 60).toString().padStart(2, "0")}`
                              : otpSent
                                ? "Resend OTP"
                                : "Send OTP"}
                        </button>
                      )}
                    </div>
                    {otpRetryTimer > 0 && (
                      <p className="mt-2 text-sm text-gray-600">
                        Please wait {Math.floor(otpRetryTimer / 60)}:
                        {(otpRetryTimer % 60).toString().padStart(2, "0")}{" "}
                        before requesting a new OTP
                      </p>
                    )}
                    {errors.mobileNo && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.mobileNo}
                      </p>
                    )}
                  </div>

                  {otpSent && !isMobileVerified && (
                    <div>
                      <label
                        htmlFor="verificationCode"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Verification Code{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          id="verificationCode"
                          name="verificationCode"
                          value={verificationCode}
                          onChange={(e) => {
                            setVerificationCode(e.target.value);
                            if (errors.verificationCode) {
                              setErrors({ ...errors, verificationCode: "" });
                            }
                          }}
                          className={`text-sm font-medium w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                            errors.verificationCode
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                          placeholder="Enter 6-digit OTP"
                          maxLength={6}
                        />
                        <button
                          type="button"
                          onClick={handleVerifyOTP}
                          disabled={isMobileVerified || otpVerifying}
                          className={`px-6 py-3 rounded-lg font-semibold transition duration-300 whitespace-nowrap  disabled:opacity-50 ${
                            isMobileVerified
                              ? "bg-green-600 text-white cursor-not-allowed"
                              : "bg-green-600 text-white hover:bg-green-700"
                          }`}
                        >
                          {otpVerifying
                            ? "Verifying..."
                            : isMobileVerified
                              ? "✓ Verified"
                              : "Verify"}
                        </button>
                      </div>
                      {errors.verificationCode && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.verificationCode}
                        </p>
                      )}
                      {isMobileVerified && (
                        <p className="mt-2 text-sm text-green-600 font-medium">
                          ✓ Mobile number verified successfully
                        </p>
                      )}
                    </div>
                  )}

                  {errors.mobileVerification && (
                    <div className="bg-yellow-50 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                      {errors.mobileVerification}
                    </div>
                  )}

                  {isMobileVerified && (
                    <>
                      <div>
                        <label
                          htmlFor="password"
                          className="block text-sm font-semibold text-gray-700 mb-2"
                        >
                          Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                              className="h-5 w-5 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                              />
                            </svg>
                          </div>
                          <input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            required
                            value={formData.password}
                            onChange={handleChange}
                            className={`text-sm font-medium w-full px-3 py-2 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                              errors.password
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            placeholder="Enter password (min. 8 characters)"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                          >
                            {showPassword ? (
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            )}
                          </button>
                        </div>
                        {errors.password && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.password}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="password"
                          className="block text-sm font-semibold text-gray-700 mb-2"
                        >
                          Verify Password{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                              className="h-5 w-5 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                              />
                            </svg>
                          </div>
                          <input
                            id="verifyPassword"
                            name="verifyPassword"
                            type={showVerifyPassword ? "text" : "password"}
                            autoComplete="current-password"
                            required
                            value={formData.verifyPassword}
                            onChange={handleChange}
                            className={`text-sm font-medium w-full px-3 py-2 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${
                              errors.verifyPassword
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            placeholder="Confirm password"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowVerifyPassword(!showVerifyPassword)
                            }
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                            aria-label={
                              showVerifyPassword
                                ? "Hide password"
                                : "Show password"
                            }
                          >
                            {showVerifyPassword ? (
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            )}
                          </button>
                        </div>
                        {errors.verifyPassword && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.verifyPassword}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Agreement */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Terms and Conditions
                </h3>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 max-h-96 overflow-y-auto">
                  <div className="prose prose-sm max-w-none">
                    <h4 className="font-bold text-gray-900 mb-4">
                      Terms and Conditions
                    </h4>
                    <p className="text-gray-700 mb-4">
                      අපගේ පන්ති සමඟ සම්බන්ධ වීමට පෙර පහත සරල කොන්දේසි කියවා
                      තේරුම් ගන්න:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                      <li>
                        සත්‍ය තොරතුරු: ලියාපදිංචියේදී ඔබ ලබාදෙන සියලුම විස්තර
                        නිවැරදි සහ සත්‍ය විය යුතුය.
                      </li>
                      <li>
                        පැමිණීම සහ වැඩ: නිසි වේලාවට පන්තිවලට සහභාගී වීම සහ
                        ලබාදෙන වැඩ (Assignments) නියමිත පරිදි නිම කිරීම
                        අත්‍යවශ්‍ය වේ.
                      </li>
                      <li>
                        ගාස්තු ගෙවීම්: පන්ති ගාස්තු අදාළ කාලසටහනට අනුව නියමිත
                        දිනට පෙර ගෙවා අවසන් කළ යුතුය.
                      </li>
                      <li>
                        විනය සහ නීති: පන්තියේ විනය නීති කඩ කරන සිසුන් පන්තියෙන්
                        ඉවත් කිරීමේ අයිතිය ආයතනය සතුය.
                      </li>
                      <li>
                        තොරතුරු රහස්‍යභාවය: ඔබගේ පෞද්ගලික තොරතුරු අධ්‍යාපනික
                        කටයුතු සඳහා මිස වෙනත් කිසිදු බාහිර පාර්ශවයකට ලබා නොදෙන
                        බවට අපි සහතික වෙමු.
                      </li>
                      <li>
                        ගිණුම් ආරක්ෂාව: ඔබගේ වෙබ් අඩවි පිවිසුම් තොරතුරු (Login
                        Details) රහසිගතව තබා ගැනීම ඔබේ වගකීමකි.
                      </li>
                      <li>
                        විස්තර යාවත්කාලීන කිරීම: දුරකථන අංක හෝ පෞද්ගලික තොරතුරු
                        වෙනස් වුවහොත් එය වහාම අප වෙත දැනුම් දිය යුතුය.
                      </li>
                    </ul>
                    <p className="text-gray-700">
                      ඉහත දැක්වෙන සියලුම නියමයන් සහ කොන්දේසි කියවා තේරුම් ගත් අතර, මම ඒවාට එකඟ වෙමි.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="agreementAccepted"
                    name="agreementAccepted"
                    checked={formData.agreementAccepted}
                    onChange={handleChange}
                    className="mt-1 h-5 w-5 text-primary-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor="agreementAccepted"
                    className="ml-3 text-sm text-gray-700"
                  >
                    I have read and agree to the terms and conditions{" "}
                    <span className="text-red-500">*</span>
                  </label>
                </div>
                {errors.agreementAccepted && (
                  <p className="text-sm text-red-500">
                    {errors.agreementAccepted}
                  </p>
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <div>
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition duration-300"
                  >
                    Previous
                  </button>
                )}
              </div>
              <div className="flex gap-4">
                {currentStep < 4 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition duration-300 shadow-lg hover:shadow-xl"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-8 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Registering..." : "Register"}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Login Link */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-primary-600 font-semibold hover:text-primary-700"
            >
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
